import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';

export const Tabs = TabsPrimitive.Root;

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const listRef = React.useRef<HTMLDivElement>(null);
  const gliderRef = React.useRef<HTMLSpanElement>(null);

  const updateGlider = React.useCallback((instant = false) => {
    const list = listRef.current;
    const glider = gliderRef.current;
    if (!list || !glider) return;

    const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;

    if (instant) glider.style.transitionDuration = '0ms';
    glider.style.width = `${active.offsetWidth}px`;
    glider.style.transform = `translateX(${active.offsetLeft}px)`;
    if (instant) {
      glider.getBoundingClientRect(); // force reflow before restoring transition
      glider.style.transitionDuration = '';
    }
  }, []);

  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    updateGlider(true);

    const observer = new MutationObserver(() => updateGlider());
    observer.observe(list, { attributes: true, subtree: true, attributeFilter: ['aria-selected'] });
    return () => observer.disconnect();
  }, [updateGlider]);

  return (
    <TabsPrimitive.List
      ref={(el) => {
        (listRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) ref.current = el;
      }}
      className={cn(
        'relative inline-flex items-center gap-1 rounded-pill border border-border bg-muted p-1 text-sm',
        className,
      )}
      {...props}
    >
      <span
        ref={gliderRef}
        aria-hidden
        className="pointer-events-none absolute bottom-1 top-1 left-0 rounded-pill bg-background shadow-sm transition-[width,transform] duration-200 ease-out"
      />
      {children}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = 'TabsList';

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'relative z-10 inline-flex items-center justify-center rounded-pill px-4 py-1.5 font-heading text-sm font-semibold tracking-tight text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:text-foreground',
      'hover:text-foreground',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    // Radix moves focus to the active TabsContent after a tab change (for
    // screen-reader announcement). That's a programmatic focus; rendering
    // a visible ring around the whole panel looks like the panel itself is
    // selected. Suppress the ring while keeping the panel reachable for AT.
    className={cn('rounded-md focus-visible:outline-none', className)}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';
